import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Globe, Copy, ExternalLink, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public Coach Profile toggle.
 *
 * Wraps the existing `POST /api/coach/(enable|disable)-public-profile`
 * endpoints so the coach can turn their `/coach/{slug}` share link on or
 * off without going through the Share flow. Displays the resulting URL
 * with a copy button while the toggle is on.
 */
export const PublicProfileSection = ({ user, onUpdate }) => {
  const initialEnabled = !!user?.public_profile_enabled;
  const initialSlug = user?.public_slug || null;
  const [enabled, setEnabled] = useState(initialEnabled);
  const [slug, setSlug] = useState(initialSlug);
  const [busy, setBusy] = useState(false);

  const shareUrl = slug ? `${window.location.origin}/coach/${slug}` : null;

  const handleToggle = async (next) => {
    setBusy(true);
    try {
      if (next) {
        const resp = await axios.post(`${API}/coach/enable-public-profile`);
        setSlug(resp.data?.public_slug || null);
        setEnabled(true);
        onUpdate?.({
          public_profile_enabled: true,
          public_slug: resp.data?.public_slug || null,
        });
        toast.success('Public profile enabled');
      } else {
        await axios.post(`${API}/coach/disable-public-profile`);
        setEnabled(false);
        onUpdate?.({ public_profile_enabled: false });
        toast.success('Public profile disabled — link is now private');
      }
    } catch (err) {
      console.error('Failed to toggle public profile:', err);
      toast.error('Could not update — please try again');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Public profile link copied');
    } catch {
      toast.error('Could not copy — long-press to select manually');
    }
  };

  return (
    <Card data-testid="public-profile-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
            <Globe className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
            Public Coach Profile
          </CardTitle>
          <div className="flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />}
            <Label htmlFor="public-profile-toggle" className="text-sm text-gray-600">
              {enabled ? 'On' : 'Off'}
            </Label>
            <Switch
              id="public-profile-toggle"
              checked={enabled}
              onCheckedChange={handleToggle}
              disabled={busy}
              data-testid="public-profile-toggle"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-600 leading-relaxed">
          Turn on a read-only public share link that anyone can view — no login required. Shows your name,
          current team, bio, qualifications and career stats. Great for sharing with parents, club officials
          or on socials. Turn off any time.
        </p>
        {enabled && shareUrl && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 flex items-center gap-2 flex-wrap" data-testid="public-profile-url-row">
            <code className="text-xs sm:text-sm text-emerald-900 break-all flex-1 min-w-0" data-testid="public-profile-url">
              {shareUrl}
            </code>
            <div className="flex gap-1 shrink-0">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopy}
                className="rounded-lg"
                data-testid="copy-public-profile-url-btn"
                title="Copy link"
              >
                <Copy className="w-3.5 h-3.5" strokeWidth={2} />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => window.open(shareUrl, '_blank', 'noopener,noreferrer')}
                className="rounded-lg"
                data-testid="open-public-profile-btn"
                title="Open in new tab"
              >
                <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PublicProfileSection;
